import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type RefObject } from "react";
import {
  FEEDBACK_ATTACHMENT_MAX_COUNT,
  FEEDBACK_ATTACHMENT_TYPES,
  acceptFeedbackAttachments,
  feedbackAttachmentsFromPaste,
  feedbackPasteBelongsToPanel,
} from "@keydials/api-client";
import { Icon } from "../Icon";

/**
 * Screenshot / document attachments on a feedback submission (KEY-261). The rules (which types, how
 * big, how many) live in `@keydials/api-client` so this app, the admin app and the API all agree; this
 * file is the user app's wiring and chrome around them.
 */
export interface FeedbackAttachmentsState {
  files: File[];
  errors: string[];
  /**
   * Put on the element that bounds the feedback panel. The paste listener is on `document`, not on this
   * element, so it fires wherever the caret is; the ref is how a paste aimed at the panel is told from
   * one aimed at a field elsewhere in the app.
   */
  surfaceRef: RefObject<HTMLDivElement | null>;
  add: (incoming: FileList | File[] | null) => void;
  remove: (index: number) => void;
  reset: () => void;
}

export function useFeedbackAttachments(): FeedbackAttachmentsState {
  // Files and errors move together, in one object: setting errors from inside a `setFiles` updater made
  // that updater impure, which React 19's StrictMode double-invokes.
  const [staged, setStaged] = useState<{ files: File[]; errors: string[] }>({ files: [], errors: [] });
  const surfaceRef = useRef<HTMLDivElement | null>(null);

  const add = useCallback((incoming: FileList | File[] | null) => {
    const list = incoming ? Array.from(incoming) : [];
    if (list.length === 0) return;
    setStaged((current) => {
      const outcome = acceptFeedbackAttachments(current.files, list);
      return { files: outcome.accepted, errors: outcome.errors };
    });
  }, []);

  // KEY-500: a `paste` dispatches at the focused node, so the old handler on the form's own element only
  // fired when focus was already inside it — and this form is a dock you are meant to keep clicking away
  // from, so most of the time it wasn't. Listening on the document catches the paste wherever the caret
  // is; the hook is mounted only while the panel is expanded, so the listener's life is the panel's.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      // A field that has already handled its own paste (the admin app's rich-text editor) keeps winning.
      if (e.defaultPrevented) return;
      if (!feedbackPasteBelongsToPanel(e.target, surfaceRef.current)) return;
      // Only intercept when the clipboard actually carries an image; a plain-text paste into a textarea
      // has to keep working exactly as before.
      const images = feedbackAttachmentsFromPaste(e.clipboardData, String(Date.now()));
      if (images.length === 0) return;
      e.preventDefault();
      add(images);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [add]);

  const remove = useCallback((index: number) => {
    setStaged((current) => ({ files: current.files.filter((_, i) => i !== index), errors: [] }));
  }, []);

  const reset = useCallback(() => setStaged({ files: [], errors: [] }), []);

  return { files: staged.files, errors: staged.errors, surfaceRef, add, remove, reset };
}

/** The pick/drop affordance plus a chip per staged file (thumbnail for images). */
export function AttachmentTray({ state }: { state: FeedbackAttachmentsState }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const full = state.files.length >= FEEDBACK_ATTACHMENT_MAX_COUNT;

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    state.add(e.dataTransfer?.files ?? null);
  }

  return (
    <div style={{ marginTop: 16 }}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text)", margin: "0 0 6px" }}>
        Attachments <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>— optional</span>
      </label>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        style={{
          border: `var(--border-w) dashed ${dragging ? "var(--accent-border)" : "var(--border)"}`,
          borderRadius: "var(--radius-sm)",
          background: dragging ? "var(--accent-soft)" : "var(--surface-2)",
          padding: "12px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
          {full ? `Limit of ${FEEDBACK_ATTACHMENT_MAX_COUNT} files reached` : "Paste a screenshot, or drop files here"}
        </span>
        <button
          type="button"
          disabled={full}
          onClick={() => inputRef.current?.click()}
          style={{
            padding: "6px 12px",
            borderRadius: "var(--radius-sm)",
            cursor: full ? "default" : "pointer",
            font: "inherit",
            fontSize: 12.5,
            fontWeight: 600,
            border: "var(--border-w) solid var(--border)",
            background: "var(--surface)",
            color: "var(--text)",
            opacity: full ? 0.5 : 1,
            whiteSpace: "nowrap",
          }}
        >
          Add files
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={FEEDBACK_ATTACHMENT_TYPES.join(",")}
          style={{ display: "none" }}
          onChange={(e) => {
            state.add(e.target.files);
            e.target.value = ""; // so re-picking the same file fires change again
          }}
        />
      </div>

      {state.files.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
          {state.files.map((file, i) => (
            <AttachmentChip key={`${file.name}-${file.size}-${i}`} file={file} onRemove={() => state.remove(i)} />
          ))}
        </div>
      )}

      {state.errors.map((message) => (
        <p key={message} style={{ margin: "6px 0 0", color: "var(--danger, #d64545)", fontSize: 12.5 }}>
          {message}
        </p>
      ))}
    </div>
  );
}

function AttachmentChip({ file, onRemove }: { file: File; onRemove: () => void }) {
  const isImage = file.type.startsWith("image/");
  // Revoked on unmount: an object URL pins the whole blob in memory until it is.
  const preview = useMemo(() => (isImage ? URL.createObjectURL(file) : null), [file, isImage]);
  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        border: "var(--border-w) solid var(--border)",
        borderRadius: "var(--radius-sm)",
        padding: "4px 8px 4px 4px",
        fontSize: 12.5,
        maxWidth: 210,
        background: "var(--surface-2)",
      }}
    >
      {preview ? (
        <img src={preview} alt="" style={{ width: 30, height: 30, objectFit: "cover", borderRadius: 4, display: "block" }} />
      ) : (
        <span style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)" }}>
          <Icon name="file" size={16} />
        </span>
      )}
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={file.name}>
        {file.name}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${file.name}`}
        style={{ border: 0, background: "none", cursor: "pointer", color: "var(--text-muted)", padding: 0, lineHeight: 1, font: "inherit", fontSize: 15 }}
      >
        ×
      </button>
    </span>
  );
}
